import { useState, useEffect, useRef } from "react";
import { useIG } from "../lib/igContext";
import { ChevronDown, Globe, Instagram, Check, Plus } from "lucide-react";
import { Link } from "react-router-dom";

export default function AccountSwitcher() {
  const { accounts, selectedId, isGlobal, selected, select } = useIG();
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    const onClick = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, []);

  const label = isGlobal ? "Vue globale" : (selected?.display_name || `@${selected?.handle}`);
  const sub = isGlobal ? `${accounts.length} compte${accounts.length > 1 ? "s" : ""}` : `@${selected?.handle}`;

  return (
    <div ref={ref} className="relative" data-testid="account-switcher">
      <button onClick={() => setOpen(!open)} data-testid="account-switcher-btn"
        className="flex items-center gap-3 px-3 py-2 border border-[#1E1E24] hover:border-[#CCFF00]/30 rounded-sm transition min-w-[210px] bg-[#0A0A0C]">
        <div className="w-7 h-7 rounded-full bg-gradient-to-br from-[#CCFF00] to-[#88AA00] flex items-center justify-center shrink-0">
          {isGlobal ? <Globe className="w-3.5 h-3.5 text-black" strokeWidth={2.2} /> : <Instagram className="w-3.5 h-3.5 text-black" strokeWidth={2.2} />}
        </div>
        <div className="flex-1 text-left min-w-0">
          <div className="text-xs text-white font-medium truncate">{label}</div>
          <div className="text-[10px] font-mono text-zinc-500 truncate uppercase tracking-wider">{sub}</div>
        </div>
        <ChevronDown className={`w-3.5 h-3.5 text-zinc-500 transition ${open ? "rotate-180" : ""}`} />
      </button>

      {open && (
        <div className="absolute left-0 top-full mt-1 w-72 bg-[#0A0A0C] border border-[#1E1E24] rounded-sm overflow-hidden z-40 shadow-2xl" data-testid="account-switcher-menu">
          <button onClick={() => { select("global"); setOpen(false); }}
            data-testid="select-global"
            className={`w-full flex items-center gap-3 px-3 py-2.5 text-sm transition ${
              isGlobal ? "bg-[#CCFF00]/10 text-[#CCFF00]" : "text-zinc-300 hover:bg-white/[0.04]"
            }`}>
            <Globe className="w-4 h-4" strokeWidth={1.5} />
            <div className="flex-1 text-left">
              <div className="font-medium">Vue globale</div>
              <div className="text-[10px] font-mono uppercase tracking-wider text-zinc-500 mt-0.5">Tous les comptes</div>
            </div>
            {isGlobal && <Check className="w-4 h-4" />}
          </button>

          <div className="border-t border-[#1E1E24] max-h-72 overflow-y-auto scrollbar-thin">
            {accounts.length === 0 ? (
              <div className="px-3 py-4 text-xs text-zinc-500 text-center">Aucun compte connecté.</div>
            ) : accounts.map((a, i) => (
              <button key={a.id} onClick={() => { select(a.id); setOpen(false); }}
                data-testid={`select-account-${i}`}
                className={`w-full flex items-center gap-3 px-3 py-2.5 text-sm transition ${
                  selectedId === a.id ? "bg-[#CCFF00]/10 text-[#CCFF00]" : "text-zinc-300 hover:bg-white/[0.04]"
                }`}>
                <div className="w-6 h-6 rounded-full bg-zinc-800 flex items-center justify-center shrink-0 text-[10px] font-bold uppercase">
                  {(a.display_name || a.handle).slice(0, 1)}
                </div>
                <div className="flex-1 text-left min-w-0">
                  <div className="font-medium truncate">{a.display_name || a.handle}</div>
                  <div className="text-[10px] font-mono uppercase tracking-wider text-zinc-500 truncate">@{a.handle} · {a.followers || 0} abonnés</div>
                </div>
                <span className={`text-[8px] font-mono uppercase tracking-wider px-1.5 py-0.5 rounded-sm shrink-0 ${
                  a.status === "connected" ? "bg-green-500/15 text-green-400" :
                  a.status === "token_expired" ? "bg-orange-500/15 text-orange-400" :
                  a.status === "api_error" ? "bg-red-500/15 text-red-400" :
                  "bg-zinc-700/40 text-zinc-400"
                }`}>{a.status}</span>
                {selectedId === a.id && <Check className="w-3.5 h-3.5 shrink-0" />}
              </button>
            ))}
          </div>

          <Link to="/instagram-accounts" onClick={() => setOpen(false)} data-testid="manage-accounts-link"
            className="flex items-center gap-2 px-3 py-2.5 border-t border-[#1E1E24] text-xs text-[#CCFF00] hover:bg-[#CCFF00]/5 transition font-mono uppercase tracking-wider">
            <Plus className="w-3.5 h-3.5" /> Gérer les comptes
          </Link>
        </div>
      )}
    </div>
  );
}
